import { useCallback, useMemo } from 'react';
import type { Image3D } from '~/domain/patient/image3d';
import type { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { useActiveFusionWriter } from '~/hooks/target/useActiveFusionWriter';
import { usePrimaryRowPairView } from '~/hooks/usePrimaryRowPairView';

export type ComparisonView = 'apart' | 'blend';

export interface PrimaryRowComparison {
    readonly pairedImage: Image3D | undefined;
    readonly apart: boolean;
    show(
        image: Image3D,
        registration: RigidRegistration | undefined,
        view: ComparisonView
    ): boolean;
    end(image: Image3D): void;
}

export function usePrimaryRowComparison(): PrimaryRowComparison {
    const primaryImage = useSelectedPrimaryImage();
    const { secondaryImage } = useActiveFusionTarget();
    const { fuse, clear } = useActiveFusionWriter();
    const { apart, requestApart } = usePrimaryRowPairView();

    const show = useCallback(
        (
            image: Image3D,
            registration: RigidRegistration | undefined,
            view: ComparisonView
        ): boolean => {
            if (!primaryImage || image.seriesUid === primaryImage.seriesUid) {
                return false;
            }
            if (!fuse(primaryImage, image, registration)) {
                return false;
            }
            if (view === 'blend') {
                getDatasetEnvironmentRegistry().setBlend(
                    primaryImage.seriesUid,
                    { mode: 'blend' }
                );
            }
            requestApart(view === 'apart');
            return true;
        },
        [primaryImage, fuse, requestApart]
    );

    const end = useCallback(
        (image: Image3D) => {
            if (
                !primaryImage ||
                secondaryImage?.seriesUid !== image.seriesUid
            ) {
                return;
            }
            clear(primaryImage);
            requestApart(false);
        },
        [primaryImage, secondaryImage, clear, requestApart]
    );

    return useMemo(
        () => ({ pairedImage: secondaryImage, apart, show, end }),
        [secondaryImage, apart, show, end]
    );
}
